<template>
  <div class="exec-list-create-pick-call tw-relative tw-min-w-[820px] tw-max-w-[100%]">
    <div class="tw-mb-3 tw-flex tw-items-center tw-gap-3 tw-flex-wrap">
      <CustomInput
        :value="searchTerm"
        :isSearch="true"
        placeholder="Поиск (ЖК, проект, группа работ, тип помещения)"
        v-debounce:300="onSearchChange"
        class="tw-min-w-[260px]"
      />
      <span class="tw-text-grey-7 tw-text-sm">
        Групп: {{ filteredGroups.length }} · Вызовов: {{ filteredHelpCallsTotal }}
      </span>
    </div>

    <q-linear-progress v-if="isLoading" indeterminate />

    <div v-if="!isLoading && !filteredGroups.length" class="tw-py-8">
      <NotFoundText text="Принятых вызовов не найдено" />
    </div>

    <div
      v-if="!isLoading && filteredGroups.length"
      class="exec-list-create-pick-call__list tw-divide-y tw-divide-grey-4 tw-border tw-border-grey-4 tw-rounded"
    >
      <div
        v-for="row in filteredGroups"
        :key="row.groupKey"
        class="exec-list-create-pick-call__row tw-flex tw-items-stretch tw-gap-3 tw-p-3"
      >
        <div class="tw-flex-1 tw-min-w-0">
          <div class="tw-text-sm tw-text-grey-8 tw-whitespace-normal tw-break-words">
            <div v-if="row.work_set_check_group_name" class="tw-font-medium">
              {{ row.work_set_check_group_name }}
            </div>
            <div v-if="row.placement_type_name" class="tw-mt-1 tw-text-grey-7">
              Тип помещения: {{ row.placement_type_name }}
            </div>
            <div class="tw-mt-1 tw-text-xs tw-text-grey-6">
              Вызовов в группе: {{ row.helpCallsCount }}
            </div>
          </div>
        </div>
        <div class="tw-flex tw-flex-shrink-0 tw-flex-col tw-items-end tw-gap-2 tw-min-w-[240px]">
          <div class="tw-flex tw-items-end tw-gap-2 tw-w-full">
            <CustomSelect
              class="tw-flex-1 tw-min-w-[140px]"
              label="Этаж"
              :options="row.floorOptions"
              optionValueKey="floor"
              optionLabelKey="floor_label"
              placeholder="Выберите этаж"
              :value="selectedFloorByGroupKey[row.groupKey]"
              :disabled="!row.floorOptions?.length"
              @change="(v) => onFloorSelect(row.groupKey, v)"
            />
            <q-btn
              unelevated
              no-caps
              color="primary"
              label="Сформировать"
              class="tw-flex-shrink-0"
              :loading="pickingGroupKey === row.groupKey"
              :disable="
                (pickingGroupKey != null && pickingGroupKey !== row.groupKey) ||
                selectedFloorByGroupKey[row.groupKey] == null ||
                selectedFloorByGroupKey[row.groupKey] === ''
              "
              @click="onPickGroup(row)"
            />
          </div>
          <span
            v-if="execListHintForRow(row)"
            class="tw-text-xs tw-text-grey-7 tw-italic tw-text-right tw-w-full"
          >
            {{ execListHintForRow(row) }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CustomInput from "@/components/form/CustomInput";
import CustomSelect from "@/components/form/CustomSelect";
import NotFoundText from "@/components/common/NotFoundText";
import { useCustomSnackbar } from "@/_helpers/hooks/useCustomSnackbar";
import { useAppStore } from "@/services/store/app";
import {
  getCheckedCallsForExecList,
  getExecListScreedPlacementRows,
  getFloorWorkSets,
} from "@/views/ResidentialSettings/services";
import {
  buildExecListModalRowsFromScreedPlacementRows,
  pickWorkSetsFromFloorInfo,
} from "@/views/EmployeeTasks/services";
import { MODAL_NAMES } from "@/constants";

export default {
  name: "ExecListCreatePickCall",
  components: { CustomInput, CustomSelect, NotFoundText },
  props: {
    data: { type: Object, required: true },
    closeModal: { type: Function, required: true },
  },
  data() {
    return {
      isLoading: false,
      calls: [],
      searchTerm: "",
      pickingGroupKey: null,
      selectedFloorByGroupKey: {},
    };
  },
  setup() {
    const { showErrorSnackbar } = useCustomSnackbar();
    const appStore = useAppStore();
    return { showErrorSnackbar, appStore };
  },
  computed: {
    filteredGroups() {
      const q = (this.searchTerm || "").toLowerCase().trim();
      if (!q) return this.calls;
      return this.calls.filter((r) =>
        [
          r.resident_name,
          r.project_name,
          r.work_set_check_group_name,
          r.placement_type_name,
          String(r.helpCallsCount ?? ""),
        ]
          .filter((x) => x != null && x !== "")
          .map((x) => String(x).toLowerCase())
          .some((s) => s.includes(q))
      );
    },
    filteredHelpCallsTotal() {
      return this.filteredGroups.reduce(
        (sum, g) => sum + (g.helpCallsCount || 0),
        0
      );
    },
  },
  mounted() {
    this.loadCalls();
  },
  methods: {
    async loadCalls() {
      this.isLoading = true;
      const rows = await getCheckedCallsForExecList({
        project_id: this.data?.projectId,
        project_entrance_id: this.data?.projectEntranceId,
      });
      this.isLoading = false;
      this.calls = this.buildGroupedCalls(Array.isArray(rows) ? rows : []);
      this.applyDefaultFloorSelection(this.calls);
    },
    applyDefaultFloorSelection(groups) {
      const selected = {};
      for (const row of groups || []) {
        const opts = row.floorOptions || [];
        if (opts.length === 1 && opts[0]?.floor != null && opts[0]?.floor !== "") {
          selected[row.groupKey] = opts[0].floor;
        }
      }
      this.selectedFloorByGroupKey = selected;
    },
    onSearchChange(value) {
      this.searchTerm = value || "";
    },
    execCallGroupKey(r) {
      const wsgId = r.work_set_check_group_id;
      const ptId = r.placement_type_id;
      if (wsgId != null && wsgId !== "" && ptId != null && ptId !== "") {
        return `id:${wsgId}:${ptId}`;
      }
      const wsg = String(r.work_set_check_group_name ?? "").trim().toLowerCase();
      const pt = String(r.placement_type_name ?? "").trim().toLowerCase();
      return `n:${wsg}\0${pt}`;
    },
    entranceFloorLabel(levelNum, floorMapId, floor) {
      const opts = this.data?.floorOptions;
      if (!Array.isArray(opts) || !opts.length) {
        return `Этаж ${floor ?? levelNum}`;
      }
      const byLevel = opts.find(
        (f) => f != null && Number(f.floor) === Number(levelNum)
      );
      if (byLevel?.floor_name) return byLevel.floor_name;
      if (floorMapId != null && floorMapId !== "") {
        const byMap = opts.find(
          (f) => f != null && Number(f.floor_map_id) === Number(floorMapId)
        );
        if (byMap?.floor_name) return byMap.floor_name;
      }
      return `Этаж ${levelNum}`;
    },
    floorOptionsFromGroupRows(levelNumToFloorMeta) {
      if (!levelNumToFloorMeta || !Object.keys(levelNumToFloorMeta).length) {
        return [];
      }
      return Object.entries(levelNumToFloorMeta)
        .map(([levelNum, meta]) => ({
          floor: Number(levelNum),
          floor_label: `Этаж ${meta?.floor}`,
          floor_name: this.entranceFloorLabel(levelNum, meta?.floor_map_id, meta?.floor),
          floor_map_id: meta?.floor_map_id ?? null,
          has_active_exec_list: !!meta?.has_active_exec_list,
          linked_exec_list_dates: Array.isArray(meta?.linked_exec_list_dates)
            ? meta.linked_exec_list_dates
            : [],
        }))
        .filter((o) => !Number.isNaN(o.floor))
        .sort((a, b) => a.floor - b.floor);
    },
    buildGroupedCalls(rows) {
      const map = new Map();
      for (const r of rows) {
        const groupKey = this.execCallGroupKey(r);
        if (!map.has(groupKey)) {
          map.set(groupKey, {
            groupKey,
            work_set_check_group_id: r.work_set_check_group_id,
            work_set_check_group_name: r.work_set_check_group_name ?? "",
            placement_type_id: r.placement_type_id,
            placement_type_name: r.placement_type_name ?? "",
            resident_name: r.resident_name,
            project_name: r.project_name,
            project_id: r.project_id,
            helpCallLevelsMap: new Map(),
            helpCallLevelsMeta: new Map(),
            levelNumToFloorMeta: {},
          });
        }
        const g = map.get(groupKey);
        if (r.help_call_id != null && r.help_call_id !== "") {
          const hid = Number(r.help_call_id);
          if (!Number.isNaN(hid)) {
            let ln = null;
            if (r.level_num != null && r.level_num !== "") {
              const n = Number(r.level_num);
              if (!Number.isNaN(n)) ln = n;
            }
            g.helpCallLevelsMap.set(hid, ln);
            const hasExec =
              r.has_active_exec_list === true ||
              r.has_active_exec_list === 1 ||
              r.has_active_exec_list === "true";
            const docDate =
              r.linked_exec_list_doc_date != null &&
              String(r.linked_exec_list_doc_date).trim() !== ""
                ? String(r.linked_exec_list_doc_date).trim()
                : null;
            g.helpCallLevelsMeta.set(hid, {
              level_num: ln,
              floor_map_id: r.floor_map_id ?? null,
              has_active_exec_list: hasExec,
              linked_exec_list_doc_date: docDate,
            });
          }
        }
        if (r.level_num != null && r.level_num !== "") {
          const lv = Number(r.level_num);
          if (!Number.isNaN(lv)) {
            const prev = g.levelNumToFloorMeta[String(lv)] || {
              floor_map_id: null,
              has_active_exec_list: false,
              linked_exec_list_dates: [],
              floor: r.floor
            };
            const floorMapId =
              r.floor_map_id != null && r.floor_map_id !== ""
                ? r.floor_map_id
                : prev.floor_map_id;
            const hasExec =
              r.has_active_exec_list === true ||
              r.has_active_exec_list === 1 ||
              r.has_active_exec_list === "true";
            const dates = [...(prev.linked_exec_list_dates || [])];
            if (
              hasExec &&
              r.linked_exec_list_doc_date != null &&
              String(r.linked_exec_list_doc_date).trim() !== ""
            ) {
              dates.push(String(r.linked_exec_list_doc_date).trim());
            }
            g.levelNumToFloorMeta[String(lv)] = {
              floor_map_id: floorMapId ?? prev.floor_map_id,
              has_active_exec_list: prev.has_active_exec_list || hasExec,
              linked_exec_list_dates: [...new Set(dates)],
              floor: r.floor
            };
          }
        }
      }
      return Array.from(map.values())
        .map((g) => {
          const help_call_ids = Array.from(g.helpCallLevelsMap.keys()).sort(
            (a, b) => Number(a) - Number(b)
          );
          const helpCallsWithFloors = Array.from(g.helpCallLevelsMap.entries()).map(
            ([help_call_id, level_num]) => {
              const m = g.helpCallLevelsMeta.get(help_call_id) || {};
              return {
                help_call_id,
                level_num,
                floor_map_id: m.floor_map_id ?? null,
                has_active_exec_list: !!m.has_active_exec_list,
                linked_exec_list_doc_date: m.linked_exec_list_doc_date ?? null,
              };
            }
          );
          const floorOptions = this.floorOptionsFromGroupRows(g.levelNumToFloorMeta);
          return {
            groupKey: g.groupKey,
            work_set_check_group_id: g.work_set_check_group_id,
            work_set_check_group_name: g.work_set_check_group_name,
            placement_type_id: g.placement_type_id,
            placement_type_name: g.placement_type_name,
            resident_name: g.resident_name,
            project_name: g.project_name,
            project_id: g.project_id,
            help_call_ids,
            helpCallsWithFloors,
            helpCallsCount: help_call_ids.length,
            floorOptions,
            levelNumToFloorMeta: g.levelNumToFloorMeta,
          };
        })
        .filter((g) => g.helpCallsCount > 0);
    },
    onFloorSelect(groupKey, value) {
      this.selectedFloorByGroupKey = {
        ...this.selectedFloorByGroupKey,
        [groupKey]: value,
      };
    },
    execListHintForRow(row) {
      const floorVal = this.selectedFloorByGroupKey[row.groupKey];
      if (floorVal == null || floorVal === "") return null;
      const opt = (row.floorOptions || []).find(
        (o) => Number(o.floor) === Number(floorVal)
      );
      if (!opt?.has_active_exec_list) return null;
      const dates = opt.linked_exec_list_dates || [];
      if (dates.length === 1) {
        return `Уже есть исп. схема (${dates[0]}), будет заменена`;
      }
      if (dates.length > 1) {
        return "По части вызовов на этаже уже есть исп. схема, будет заменена";
      }
      return "Уже есть исп. схема, будет заменена";
    },
    filterHelpCallIdsByFloor(row, floorVal) {
      const ln = Number(floorVal);
      if (Number.isNaN(ln)) return [];
      return (row.helpCallsWithFloors || [])
        .filter((h) => Number(h.level_num) === ln)
        .map((h) => Number(h.help_call_id))
        .filter((id) => !Number.isNaN(id));
    },
    async onPickGroup(row) {
      const floorVal = this.selectedFloorByGroupKey[row.groupKey];
      if (floorVal == null || floorVal === "") {
        this.showErrorSnackbar("Выберите этаж");
        return;
      }
      const floorOpt = (row.floorOptions || []).find(
        (o) => Number(o.floor) === Number(floorVal)
      );
      const helpCallIds = this.filterHelpCallIdsByFloor(row, floorVal);
      if (!helpCallIds.length) {
        this.showErrorSnackbar("Нет вызовов для выбранного этажа");
        return;
      }
      const projectId = row.project_id ?? this.data?.projectId;
      const floorMapId = floorOpt?.floor_map_id;
      if (!projectId || !floorMapId) {
        this.showErrorSnackbar("Не указан проект или этаж");
        return;
      }

      this.pickingGroupKey = row.groupKey;
      const isProjectDirty = this.data?.projectTypeCode === "PROJECT_DIRTY";
      const placementRows = isProjectDirty
        ? await getFloorWorkSets(floorMapId, {
            placement_type_id: row.placement_type_id ?? null,
            work_set_check_group_id: row.work_set_check_group_id ?? null,
          })
        : await getExecListScreedPlacementRows(floorMapId, {
            work_set_check_group_id: row.work_set_check_group_id ?? null,
          });
      this.pickingGroupKey = null;

      if (!placementRows) {
        this.showErrorSnackbar("Не удалось загрузить работы по этажу");
        return;
      }

      let modalRows = [];
      if (isProjectDirty) {
        modalRows = pickWorkSetsFromFloorInfo(
          placementRows,
          row.placement_type_id ?? null,
          row.work_set_check_group_id ?? null
        );
      } else {
        modalRows = buildExecListModalRowsFromScreedPlacementRows(
          placementRows,
          row.work_set_check_group_id
        );
      }

      if (!modalRows.length) {
        this.showErrorSnackbar("Нет работ в группе для исполнительной схемы");
        return;
      }

      const onSuccess = this.data?.onSuccess;
      this.closeModal();
      this.appStore.showModal(MODAL_NAMES.ExecListCreateDocument, {
        title: "Исполнительная схема",
        projectId,
        floorMapId,
        placementTypeId: row.placement_type_id ?? null,
        workSetCheckGroupId: row.work_set_check_group_id ?? null,
        manualCreateHelpCallIds: helpCallIds,
        rows: modalRows.map((s) => ({
          placement_type_id: s.placement_type_id,
          legend_placement_type_id: s.legend_placement_type_id,
          placement_type_name: s.placement_type_name,
          work_set_id: s.work_set_id,
          work_set_name: s.work_set_name,
          unit_name: s.unit_name,
          work_set_amount: s.work_set_amount,
        })),
        documentParams: this.data?.documentParams || {},
        openConfirmModal: (payload) =>
          this.appStore.showModal(MODAL_NAMES.CONFIRM, payload),
        onSuccess: (payload) => {
          if (typeof onSuccess === "function") onSuccess(payload);
        },
      });
    },
  },
};
</script>

<style scoped lang="scss">
.exec-list-create-pick-call {
  &__list {
    max-height: 60vh;
    overflow-y: auto;
  }

  &__row {
    transition: background-color 0.15s ease;
    &:hover {
      background-color: #f5f7fa;
    }
  }
}
</style>
